import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import moment from 'moment';
import { DialogModule } from 'primeng';
import { ISystemSettings } from '../../interfaces/system-settings';
import { BBWTMessageService, MapService, SettingsService, TaskService } from '../../services';
import { VMEComponentsModule } from '../../vme-components.module';
import { VmeCommonModule } from '../../vme-common.module';
import { VmePipesModule } from '../../vme-pipes.module';
import { minutesInterval } from '../../user-controls/datetime-picker.component';
import { ITaskWindowUpdate } from '../../interfaces/task-info';

@Component({
  selector: 'edit-task-windows-dialog',
  templateUrl: 'edit-task-windows-dialog.component.html',
  styleUrls: ['edit-task-windows-dialog.component.scss'],
  standalone: true,
  imports: [DialogModule, VMEComponentsModule, VmeCommonModule, VmePipesModule],
})
export class EditTaskWindowsDialogComponent implements OnInit {
  @Input() task: ITaskWindowUpdate;

  @Input() visible: boolean = false;

  @Output() visibleChange = new EventEmitter<boolean>();

  @Output() taskUpdated: EventEmitter<void> = new EventEmitter<void>();

  previousDropoffEarly: Date;
  previousPickupEarly: Date;
  previousPickupLate: Date;
  previousDropoffLate: Date;

  routeTime: number;

  systemSettings: ISystemSettings;
  minutesInterval: minutesInterval = 60; // default

  dateTimesChanged: boolean = false;

  savingChecks: boolean = false;

  showConfirmDates: boolean = false;

  private _pickupEarliestDateTime: Date;

  public set pickupEarliestDateTime(value: Date) {
    this.trimSeconds(value);

    this._pickupEarliestDateTime = this.roundUpTime(value);
    this.dateTimesChanged = true;
  }

  public get pickupEarliestDateTime(): Date {
    return this._pickupEarliestDateTime;
  }

  private _pickupLatestDateTime: Date;
  public set pickupLatestDateTime(value: Date) {
    this.trimSeconds(value);

    this._pickupLatestDateTime = value;
    this.dateTimesChanged = true;
  }

  public get pickupLatestDateTime(): Date {
    return this._pickupLatestDateTime;
  }

  private _dropoffEarliestDateTime: Date;
  public set dropoffEarliestDateTime(value: Date) {
    this.trimSeconds(value);

    this._dropoffEarliestDateTime = value;
    this.dateTimesChanged = true;
  }

  public get dropoffEarliestDateTime(): Date {
    return this._dropoffEarliestDateTime;
  }

  private _dropoffLatestDateTime: Date;
  public set dropoffLatestDateTime(value: Date) {
    this.trimSeconds(value);

    this._dropoffLatestDateTime = value;
    this.dateTimesChanged = true;
  }

  public get dropoffLatestDateTime(): Date {
    return this._dropoffLatestDateTime;
  }

  constructor(
    public messageService: BBWTMessageService,
    private taskService: TaskService,
    private mapService: MapService,
    private settingsService: SettingsService,
  ) {}

  ngOnInit() {
    this.settingsService.GetSystemSettings().then((data: ISystemSettings) => {
      this.systemSettings = data;
    });
  }

  alignDateTimes() {
    // ensure following dates are aligned with this one (i.e. they can't be earlier and must include travel time)
    this.pickupLatestDateTime = this.isDateEarlier(
      this.pickupLatestDateTime,
      this.pickupEarliestDateTime,
    )
      ? moment(this.pickupEarliestDateTime).toDate()
      : moment(this.pickupLatestDateTime).toDate();

    let dropoffEarly = this.addRouteTime(this.pickupEarliestDateTime);
    this.dropoffEarliestDateTime = this.isDateEarlier(this.dropoffEarliestDateTime, dropoffEarly)
      ? moment(dropoffEarly).toDate()
      : moment(this.dropoffEarliestDateTime).toDate();

    let dropoffLate = this.addRouteTime(this.pickupLatestDateTime);
    this.dropoffLatestDateTime = this.isDateEarlier(this.dropoffLatestDateTime, dropoffLate)
      ? moment(dropoffLate).toDate()
      : moment(this.dropoffLatestDateTime).toDate();
  }

  isDateEarlier(date1: Date, date2: Date) {
    return moment(date1).isBefore(date2);
  }

  getValidPickupEarliest() {
    let earliest = moment(this.pickupEarliestDateTime).toDate();
    let defEarliest = this.getDefaultPickupEarliestDateTime();

    if (earliest < defEarliest) {
      let newEarliest = this.roundUpTime(defEarliest);
      this.trimSeconds(newEarliest);

      return newEarliest;
    }

    this.trimSeconds(earliest);
    return earliest;
  }

  getValidPickupLatest(pickupEarliest: Date) {
    let latest = moment(this.pickupLatestDateTime).toDate();
    let defLatest = this.getDefaultPickupLatestDateTime(pickupEarliest);

    if (latest < defLatest) {
      let newLatest = this.roundUpTime(defLatest);
      this.trimSeconds(newLatest);

      return newLatest;
    }

    this.trimSeconds(latest);
    return latest;
  }

  getValidDropoffEarliest(pickupEarliest: Date) {
    let earliest = moment(this.dropoffEarliestDateTime).toDate();
    let defEarliest = this.addRouteTime(pickupEarliest);

    if (earliest < defEarliest) {
      let newEarliest = this.roundUpTime(defEarliest);
      this.trimSeconds(newEarliest);

      return newEarliest;
    }

    this.trimSeconds(earliest);
    return earliest;
  }

  getValidDropoffLatest(pickupLatest: Date) {
    let latest = moment(this.dropoffLatestDateTime).toDate();
    let defLatest = this.addRouteTime(pickupLatest);

    if (latest < defLatest) {
      let newLatest = this.roundUpTime(defLatest);
      this.trimSeconds(newLatest);

      return newLatest;
    }

    this.trimSeconds(latest);
    return latest;
  }

  addRouteTime(date: Date): Date {
    return this.roundUpTime(moment(date).add(this.routeTime, 's').toDate());
  }

  getDefaultPickupEarliestDateTime(): Date {
    return new Date();
    //return moment().add(24, 'h').toDate();
  }

  getDefaultPickupLatestDateTime(pickupEarliestDateTime: Date): Date {
    let createOrderTimeDayStart = this.systemSettings.createOrderTimeDayStart;
    let createOrderTimeDayEnd = this.systemSettings.createOrderTimeDayEnd;
    let createOrderPickUpLatestPlusDays = this.systemSettings.createOrderPickUpLatestPlusDays;
    let createOrderAfterEndPickUpLatestPlusDays =
      this.systemSettings.createOrderAfterEndPickUpLatestPlusDays;

    let pickupEarliestDateTimeMoment = moment(pickupEarliestDateTime);
    if (createOrderTimeDayStart) {
      let createOrderTimeDayStartMoment = moment(pickupEarliestDateTime)
        .startOf('day')
        .add(createOrderTimeDayStart, 'h');

      // 1st case
      if (pickupEarliestDateTimeMoment.isSameOrBefore(createOrderTimeDayStartMoment)) {
        if (createOrderPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime)
            .startOf('day')
            .add(createOrderPickUpLatestPlusDays, 'd')
            .add(createOrderTimeDayStart, 'h')
            .toDate();
        }
      }
    }

    if (createOrderTimeDayEnd) {
      let createOrderTimeDayEndMoment = moment(pickupEarliestDateTime)
        .startOf('day')
        .add(createOrderTimeDayEnd + 12, 'h');

      // 2nd case
      if (pickupEarliestDateTimeMoment.isSameOrBefore(createOrderTimeDayEndMoment)) {
        if (createOrderPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime).add(createOrderPickUpLatestPlusDays, 'd').toDate();
        }
      }
      // 3rd case
      else {
        if (createOrderAfterEndPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime)
            .add(createOrderAfterEndPickUpLatestPlusDays, 'd')
            .toDate();
        }
      }
    }

    // default
    return moment(pickupEarliestDateTime).add(12, 'h').toDate();
  }

  roundUpTime(time: Date) {
    this.trimSeconds(time);

    if (time.getMinutes() % this.minutesInterval == 0 || time.getMinutes() == 0) {
      return time;
    }

    let min = this.minutesInterval;
    while (min < 60) {
      if (min >= time.getMinutes()) {
        break;
      }

      min += this.minutesInterval;
    }

    if (min > 60) {
      time.setMinutes(60);
    } else {
      time.setMinutes(min);
    }

    return time;
  }

  trimSeconds(dt: Date) {
    if (dt) {
      dt.setSeconds(0);
      dt.setMilliseconds(0);
    }
  }

  onTaskSave() {
    //if any of the task dates/times are outside a range that would be acceptable for a new order
    //then prompt the user with a warning and ask them if they want an auto-completed version with corrected min dates/times.
    if (!this.dateTimesChanged) {
      return;
    }

    let pickupEarly = this.getValidPickupEarliest();
    let dropoffEarly = this.getValidDropoffEarliest(pickupEarly);
    let pickupLate = this.getValidPickupLatest(pickupEarly);
    let dropoffLate = this.getValidDropoffLatest(dropoffEarly);

    if (
      !(
        this.pickupEarliestDateTime < pickupEarly ||
        this.pickupLatestDateTime < pickupLate ||
        this.dropoffEarliestDateTime < dropoffEarly ||
        this.dropoffLatestDateTime < dropoffLate
      )
    ) {
      void this.saveTaskWindows();
      return;
    }

    this.showConfirmDates = true;

    this.previousPickupEarly = new Date(this.task.pickupEarliestDateTime);
    this.previousPickupLate = new Date(this.task.pickupLatestDateTime);
    this.previousDropoffEarly = new Date(this.task.dropoffEarliestDateTime);
    this.previousDropoffLate = new Date(this.task.dropoffLatestDateTime);
  }

  undoChanges() {
    this.showConfirmDates = false;
    this.pickupEarliestDateTime = this.previousPickupEarly;
    this.pickupLatestDateTime = this.previousPickupLate;
    this.dropoffEarliestDateTime = this.previousDropoffEarly;
    this.dropoffLatestDateTime = this.previousDropoffLate;
  }

  async saveTaskWindows() {
    let taskUpdate = {
      ...this.task,
      id: this.task.id,
      pickupEarliestDateTime: moment(this.pickupEarliestDateTime).format('YYYY-MM-DD HH:mm'),
      pickupLatestDateTime: moment(this.pickupLatestDateTime).format('YYYY-MM-DD HH:mm'),
      dropoffEarliestDateTime: moment(this.dropoffEarliestDateTime).format('YYYY-MM-DD HH:mm'),
      dropoffLatestDateTime: moment(this.dropoffLatestDateTime).format('YYYY-MM-DD HH:mm'),
    };

    this.savingChecks = true;

    try {
      await this.taskService.updateTaskWindows(taskUpdate);
      this.messageService.ShowSuccess('Success', 'Task windows updated');
      this.taskUpdated.emit();
    } catch (e) {
      this.messageService.ShowError('Error', 'Unable to update task windows');
    } finally {
      this.savingChecks = false;
      this.showConfirmDates = false;
    }
  }

  closeDialog() {
    this.visibleChange.emit(false);
  }

  onShow() {
    const t = this.task;

    const timeParts = t.estimateTime.split(':');
    this.routeTime = +timeParts[0] * 60 * 60 + +timeParts[1] * 60 + +timeParts[2];

    this._pickupEarliestDateTime = moment(t.pickupEarliestDateTime).toDate();
    this._pickupLatestDateTime = moment(t.pickupLatestDateTime).toDate();
    this._dropoffEarliestDateTime = moment(t.dropoffEarliestDateTime).toDate();
    this._dropoffLatestDateTime = moment(t.dropoffLatestDateTime).toDate();
  }
}
